import { ReactNode } from "react";
import { Box, Typography, alpha, darken, useTheme } from "@mui/material";
import { ShieldOutlined } from "@mui/icons-material";

interface Props {
  label: string;
  /** Monto ya formateado (`$12,345.67`). */
  amount: string;
  /** Línea bajo el monto (`MXN / mes durante 24 meses`). */
  caption: string;
  /** Acciones a la derecha del monto: el CTA y, si aplica, un enlace secundario. */
  actions: ReactNode;
  /** Nota al pie con el escudo; puede llevar un enlace (ej. los términos del cotizador de Flux). */
  disclaimer?: ReactNode;
  /**
   * Opcional: pie con contenido propio en lugar de `disclaimer`, en la misma franja bajo la línea y sin el escudo.
   * Ej. Mercader: el check del aviso de privacidad, que se acepta en el cotizador.
   */
  footer?: ReactNode;
  /** Opcional: contenido bajo el `caption`, como el desglose de la renta del cotizador de WuWei. */
  children?: ReactNode;
  /**
   * Opcional: color de marca de la tarjeta (`--ld-blue` del diseño) cuando la marca no lo tiene en `primary.main`.
   * Pinta el halo, el borde degradado, el escudo, la etiqueta (oscurecido) y los bordes tenues. Ej. WuWei: botón
   * azul marino y tarjeta dorada (`primary.accent`). Sin él, la tarjeta queda como siempre.
   */
  brandColor?: string;
}

/** Neutros del spec (`--ld-text-2`, `--ld-text-3`); no varían por tenant. */
const TEXT_2 = "#4A5567";
const TEXT_3 = "#7A8497";

/**
 * Replica `.quote-hero` del bloque "LENDIA REDESIGN v2" de styles.css, con los overrides de Flux:
 * - fondo con un halo radial de `primary.main` en la esquina superior derecha sobre blanco frío;
 * - borde de 1px en el tono claro de marca más un borde degradado (`::before`) que se desvanece;
 * - monto de 34px en `primary.dark` y aviso legal separado por una línea abajo.
 * Los colores de marca se derivan de la paleta del tenant.
 */
export const CardResumenCotizacion = ({
  label,
  amount,
  caption,
  actions,
  disclaimer,
  footer,
  children,
  brandColor,
}: Props) => {
  const theme = useTheme();
  const brand = brandColor ?? theme.palette.primary.main;
  // Bordes tenues (`--ld-blue-100`) y etiqueta (`--ld-blue-700`): sin `brandColor`, los de siempre.
  const softBorder = brandColor ? alpha(brandColor, 0.3) : alpha(theme.palette.primary.accent, 0.35);
  const labelColor = brandColor
    ? darken(brandColor, 0.15)
    : (theme.palette.primary.hover ?? darken(theme.palette.primary.main, 0.35));

  return (
    <Box
      sx={{
        position: "relative",
        mt: "14px",
        p: { xs: "22px 20px 20px", md: "26px 30px 24px" },
        background: `radial-gradient(120% 100% at 100% 0%, ${alpha(brand, 0.05)} 0%, transparent 55%), linear-gradient(180deg, #F9FBFD 0%, #FDFEFE 100%)`,
        border: "1px solid",
        borderColor: softBorder,
        borderRadius: "14px",
        boxShadow: "0 4px 12px rgba(14,31,58,.08), 0 2px 4px rgba(14,31,58,.04)",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
        alignItems: "end",
        columnGap: 3,
        rowGap: { xs: 2, md: 0 },
        overflow: "hidden",
        // Borde degradado del diseño: más intenso arriba a la izquierda y se pierde hacia la derecha.
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          padding: "1.25px",
          background: `linear-gradient(118deg, ${alpha(brand, 0.55)} 0%, ${alpha(brand, 0.28)} 22%, ${alpha(brand, 0.08)} 48%, transparent 72%)`,
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            // `--ld-blue-700` del diseño: el hover de la marca si lo define; si no, `main` oscurecido.
            color: labelColor,
            mb: 1,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            // 34px del spec: el número grande del cotizador, fuera de la escala del theme.
            fontSize: 34,
            fontWeight: 600,
            letterSpacing: "-0.025em",
            fontVariantNumeric: "tabular-nums",
            color: "primary.dark",
            lineHeight: 1.1,
            mb: 0.5,
          }}
        >
          {amount}
        </Typography>
        <Typography sx={{ fontSize: 13, color: TEXT_2 }}>{caption}</Typography>
        {children}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "stretch", md: "flex-end" },
          gap: 1.25,
        }}
      >
        {actions}
      </Box>

      {(footer || disclaimer) && (
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mt: { xs: 0, md: 2 },
            pt: footer ? 2 : "14px",
            borderTop: "1px solid",
            borderColor: softBorder,
          }}
        >
          {footer ?? (
            <>
              <ShieldOutlined sx={{ fontSize: 13, color: brand, mt: "2px", flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, color: TEXT_3, lineHeight: 1.55 }}>{disclaimer}</Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
